import React, { useState, useEffect } from 'react';
import {
  Paper, Box, Typography, Button, Grid, Stack, TextField,
} from '@mui/material';
import { useModal } from '../../../contexts/ModalContext';
import { useBookingDetails } from '../../../contexts/BookingDetailsContext';
import PhoneInput from '../../../components/common/PhoneInput';
import { bookingTokens, cardSx, cardHeadSx, primaryButtonSx } from './bookingDetailsTheme';

// ─── Shared styles ────────────────────────────────────────────────────────────
const inlineFieldSx = {
  '& .MuiInputBase-root': { borderRadius: '9px', fontSize: '13px', background: '#fff' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: bookingTokens.border },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: bookingTokens.green, borderWidth: '1.5px',
  },
};

const saveBtnSx = {
  background: 'linear-gradient(135deg,#0a6b3e,#0d8a50)',
  color: '#fff', fontSize: 12, fontWeight: 700,
  borderRadius: '8px', px: 1.5, py: '6px',
  textTransform: 'none', border: 'none',
  boxShadow: '0 2px 6px rgba(10,107,62,0.22)',
  '&:hover': { background: '#0a6b3e' },
  '&.Mui-disabled': { background: '#a3c9b5', color: '#fff' },
};

const cancelBtnSx = {
  fontSize: 12, fontWeight: 700,
  color: bookingTokens.red,
  background: bookingTokens.redLt,
  border: `1.5px solid ${bookingTokens.redBdr}`,
  borderRadius: '8px', px: 1.5, py: '5px',
  textTransform: 'none',
  '&:hover': { background: '#fecaca' },
};

// ─── Component ────────────────────────────────────────────────────────────────
const AdditionalCustomers = ({ booking, onUpdate }) => {
  const { openSuccessModal, openErrorModal, openWarningModal } = useModal();
  const { updateAdditionalCustomer } = useBookingDetails();

  const [additionalCustomers,   setAdditionalCustomers]   = useState(booking?.additional_customers || []);
  const [addingCustomer,        setAddingCustomer]        = useState(false);
  const [editingCustomerId,     setEditingCustomerId]     = useState(null);
  const [customerForm,          setCustomerForm]          = useState({ customer_name: '', customer_email: '', customer_phone: '' });
  const [formLoading,           setFormLoading]           = useState(false);
  const [customerFormSubmitted, setCustomerFormSubmitted] = useState(false);

  useEffect(() => {
    setAdditionalCustomers(booking?.additional_customers || []);
  }, [booking?.additional_customers]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const persist = async (updated) => {
    await updateAdditionalCustomer(booking.id, updated);
    setAdditionalCustomers(updated);
    onUpdate?.(updated);
  };

  const handleCancel = () => {
    setAddingCustomer(false);
    setEditingCustomerId(null);
    setCustomerForm({ customer_name: '', customer_email: '', customer_phone: '' });
    setCustomerFormSubmitted(false);
  };

  const handleAddOtherCustomer = () => {
    if (additionalCustomers.length >= 5) {
      openWarningModal('Not Allowed!', 'Maximum 5 additional customers allowed.');
      return;
    }
    setCustomerForm({ customer_name: '', customer_email: '', customer_phone: '' });
    setAddingCustomer(true);
    setEditingCustomerId(null);
    setCustomerFormSubmitted(false);
  };

  const handleEditOtherCustomer = (c) => {
    setCustomerForm({ customer_name: c.customer_name || '', customer_email: c.customer_email || '', customer_phone: c.customer_phone || '' });
    setEditingCustomerId(c.id);
    setAddingCustomer(false);
    setCustomerFormSubmitted(false);
  };

  const handleCustomerFormChange = (e) =>
    setCustomerForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleCustomerFormSubmit = async (e) => {
    e.preventDefault();
    setCustomerFormSubmitted(true);
    if (!customerForm.customer_name.trim()) { openErrorModal('Name is required'); return; }

    const trimmed = customerForm.customer_name.trim().toLowerCase();
    const isDupe  = additionalCustomers.some(c =>
      (c.customer_name || '').toLowerCase() === trimmed && c.id !== editingCustomerId
    );
    if (isDupe) { openErrorModal('A customer with this name already exists'); return; }

    setFormLoading(true);
    try {
      const updated = editingCustomerId
        ? additionalCustomers.map(c => c.id === editingCustomerId
            ? { ...c, customer_name: customerForm.customer_name.trim(), customer_email: customerForm.customer_email.trim(), customer_phone: customerForm.customer_phone.trim() }
            : c)
        : [...additionalCustomers, {
            customer_name:  customerForm.customer_name.trim(),
            customer_email: customerForm.customer_email.trim(),
            customer_phone: customerForm.customer_phone.trim(),
            id: Date.now(),
          }];

      await persist(updated);
      setAddingCustomer(false);
      setEditingCustomerId(null);
      setCustomerForm({ customer_name: '', customer_email: '', customer_phone: '' });
      setCustomerFormSubmitted(false);
      openSuccessModal('Additional customers updated!');
    } catch {
      openErrorModal('Failed to update additional customers');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteOtherCustomer = async (id) => {
    setFormLoading(true);
    try {
      await persist(additionalCustomers.filter(c => c.id !== id));
      openSuccessModal('Additional customers updated!');
    } catch {
      openErrorModal('Failed to update additional customers');
    } finally {
      setFormLoading(false);
    }
  };

  // Allow add/edit/delete until booking is completed
  const canEdit = booking?.status !== 'completed';

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Paper elevation={0} sx={{ ...cardSx, mt: 2 }}>

      {/* Header */}
      <Box sx={cardHeadSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography component="span" sx={{ fontSize: 14 }}>👥</Typography>
          <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: bookingTokens.ink, letterSpacing: '-0.2px' }}>
            Additional Customers
          </Typography>
          {additionalCustomers.length > 0 && (
            <Box component="span" sx={{
              fontSize: 11, fontWeight: 700,
              background: bookingTokens.greenLt, color: bookingTokens.green,
              border: `1px solid ${bookingTokens.border}`,
              px: 1, py: '1px', borderRadius: 99,
            }}>
              {additionalCustomers.length}
            </Box>
          )}
        </Box>
        {!addingCustomer && editingCustomerId === null && canEdit && (
          <Button size="small" onClick={handleAddOtherCustomer} sx={{ ...primaryButtonSx, py: '6px', px: 1.5, fontSize: 12 }}>
            + Add Customer
          </Button>
        )}
      </Box>

      {/* Add form */}
      {addingCustomer && (
        <Box component="form" onSubmit={handleCustomerFormSubmit} sx={{ mx: '16px', mb: 2, p: '14px 16px', background: bookingTokens.greenLt, border: `1.5px solid ${bookingTokens.border}`, borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: bookingTokens.subtle, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            New Customer
          </Typography>
          <Grid container spacing={1.5} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <TextField label="Name" name="customer_name" value={customerForm.customer_name} onChange={handleCustomerFormChange} fullWidth required size="small" error={customerFormSubmitted && !customerForm.customer_name.trim()} helperText={customerFormSubmitted && !customerForm.customer_name.trim() ? 'Name is required' : ''} sx={inlineFieldSx} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField label="Email" name="customer_email" value={customerForm.customer_email} onChange={handleCustomerFormChange} fullWidth size="small" placeholder="Optional" sx={inlineFieldSx} />
            </Grid>
            <Grid item xs={12} md={3}>
              <PhoneInput size="small" label="Phone" value={customerForm.customer_phone} onChange={(val) => setCustomerForm(p => ({ ...p, customer_phone: val }))} required={false} />
            </Grid>
            <Grid item xs={12} md={2}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: { xs: 0, md: '4px' } }}>
                <Button type="submit" size="small" disabled={formLoading || !customerForm.customer_name.trim()} sx={saveBtnSx}>💾 Save</Button>
                <Button onClick={handleCancel} size="small" sx={cancelBtnSx}>✕</Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Empty state */}
      {additionalCustomers.length === 0 && !addingCustomer ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4, gap: 1 }}>
          <Typography sx={{ fontSize: 32, opacity: 0.3 }}>👤</Typography>
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: bookingTokens.muted }}>No additional customers added.</Typography>
        </Box>
      ) : (
        <Box sx={{ px: '16px', pb: '16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {additionalCustomers.map((c) =>
            editingCustomerId === c.id ? (

              /* Edit row */
              <Box key={c.id} component="form" id={`edit-form-${c.id}`} onSubmit={handleCustomerFormSubmit} sx={{ p: '12px 14px', background: bookingTokens.greenLt, border: `1.5px solid ${bookingTokens.border}`, borderRadius: '10px' }}>
                <Typography sx={{ fontSize: 10, fontWeight: 800, color: bookingTokens.subtle, textTransform: 'uppercase', letterSpacing: '0.4px', mb: 1.5 }}>
                  Editing — {c.customer_name}
                </Typography>
                <Grid container spacing={1.5} alignItems="flex-start">
                  <Grid item xs={12} md={4}>
                    <TextField name="customer_name" label="Name" value={customerForm.customer_name} onChange={handleCustomerFormChange} size="small" fullWidth required error={customerFormSubmitted && !customerForm.customer_name.trim()} helperText={customerFormSubmitted && !customerForm.customer_name.trim() ? 'Name is required' : ''} sx={inlineFieldSx} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField name="customer_email" label="Email" value={customerForm.customer_email} onChange={handleCustomerFormChange} size="small" fullWidth placeholder="Optional" sx={inlineFieldSx} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <PhoneInput label="Phone" size="small" value={customerForm.customer_phone} onChange={(val) => setCustomerForm(p => ({ ...p, customer_phone: val }))} required={false} />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Stack direction="row" spacing={1} sx={{ pt: { xs: 0, md: '4px' } }}>
                      <Button type="submit" form={`edit-form-${c.id}`} size="small" disabled={formLoading || !customerForm.customer_name.trim()} sx={saveBtnSx}>💾 Save</Button>
                      <Button onClick={handleCancel} size="small" sx={cancelBtnSx}>✕</Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

            ) : (

              /* Display row */
              <Box key={c.id} sx={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr auto', alignItems: 'center', gap: '8px', px: '14px', py: '10px', background: '#fff', border: `1px solid ${bookingTokens.border}`, borderRadius: '10px', transition: 'background 120ms', '&:hover': { background: '#f2f8f4' } }}>

                {/* Name */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: '7px', flexShrink: 0, background: bookingTokens.green, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>
                    {(c.customer_name || 'NA').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: bookingTokens.muted, textTransform: 'uppercase', letterSpacing: '0.4px', mb: '2px' }}>Name</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: bookingTokens.ink2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.customer_name || 'Not provided'}
                    </Typography>
                  </Box>
                </Box>

                {/* Phone */}
                <Box>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: bookingTokens.muted, textTransform: 'uppercase', letterSpacing: '0.4px', mb: '2px' }}>Phone</Typography>
                  <Typography sx={{ fontSize: 12.5, color: c.customer_phone ? bookingTokens.ink2 : bookingTokens.muted }}>{c.customer_phone || '—'}</Typography>
                </Box>

                {/* Email */}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: bookingTokens.muted, textTransform: 'uppercase', letterSpacing: '0.4px', mb: '2px' }}>Email</Typography>
                  <Typography sx={{ fontSize: 12.5, color: c.customer_email ? bookingTokens.ink2 : bookingTokens.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.customer_email || '—'}</Typography>
                </Box>

                {/* Actions - hidden when booking is completed */}
                {canEdit && (
                  <Stack direction="row" spacing={0.75}>
                    <Button size="small" onClick={() => handleEditOtherCustomer(c)} sx={{ fontSize: 11.5, fontWeight: 700, color: bookingTokens.green, background: bookingTokens.greenLt, border: `1.5px solid ${bookingTokens.border}`, borderRadius: '7px', px: 1.25, py: '4px', textTransform: 'none', minWidth: 0, '&:hover': { background: '#d1ede0' } }}>Edit</Button>
                    <Button size="small" onClick={() => handleDeleteOtherCustomer(c.id)} sx={{ fontSize: 11.5, fontWeight: 700, color: bookingTokens.red, background: bookingTokens.redLt, border: `1.5px solid ${bookingTokens.redBdr}`, borderRadius: '7px', px: 1.25, py: '4px', textTransform: 'none', minWidth: 0, '&:hover': { background: '#fecaca' } }}>Delete</Button>
                  </Stack>
                )}
              </Box>
            )
          )}
        </Box>
      )}
    </Paper>
  );
};

export default AdditionalCustomers;
